import moment from 'moment-timezone'

import { intlLocaleFor } from 'services/locale_mapping'

/**
 * Threshold in seconds for "starting soon" status (15 minutes)
 */
const STARTING_SOON_THRESHOLD_SECONDS = 900

/**
 * GraphQL MeetingStatus enum values
 */
export type MeetingStatusEnum = 'CANCELED' | 'DEFAULT' | 'NO_SHOW' | 'RESCHEDULED' | 'COMPLETED'

/**
 * GraphQL MeetingStatus constants (use instead of string literals)
 */
export const MEETING_STATUS = {
  CANCELED: 'CANCELED',
  DEFAULT: 'DEFAULT',
  NO_SHOW: 'NO_SHOW',
  RESCHEDULED: 'RESCHEDULED',
  COMPLETED: 'COMPLETED',
} as const

/**
 * UI display status (derived from GraphQL status + time calculations)
 * Past meetings with no special status (e.g. completed) show no badge; we use UPCOMING for that case (empty display text).
 */
export type DisplayStatus = 'CANCELLED' | 'IN_PROGRESS' | 'STARTING_SOON' | 'UPCOMING' | 'UP_NEXT' | 'NO_SHOW' | 'RESCHEDULED'

/**
 * Display status constants
 */
export const DISPLAY_STATUS = {
  CANCELLED: 'CANCELLED',
  IN_PROGRESS: 'IN_PROGRESS',
  STARTING_SOON: 'STARTING_SOON',
  UPCOMING: 'UPCOMING',
  UP_NEXT: 'UP_NEXT',
  NO_SHOW: 'NO_SHOW',
  RESCHEDULED: 'RESCHEDULED',
} as const

/**
 * I18n keys for display text (en.yml under publisher.meetings.header_badge).
 * Use with getDisplayLabelKey; translate via I18n.t in the component layer.
 * UPCOMING has no label (empty string).
 */
export const DISPLAY_TEXT_TRANSLATION_KEYS: Record<DisplayStatus, string> = {
  CANCELLED: 'canceled',
  IN_PROGRESS: 'in_progress',
  STARTING_SOON: 'starting_soon',
  UPCOMING: '',
  UP_NEXT: 'up_next',
  NO_SHOW: 'no_show',
  RESCHEDULED: 'rescheduled',
}

/**
 * Meeting data structure after mapNodeToMeeting transformation
 * Uses camelCase property names (transformed from GraphQL snake_case)
 */
export interface Meeting {
  uuid?: string
  name?: string
  cancelled?: boolean
  status?: MeetingStatusEnum
  startTime?: string
  endTime?: string
  past?: boolean
  [key: string]: unknown
}

/**
 * Returns true if meeting.cancelled === true OR meeting.status === MEETING_STATUS.CANCELED
 *
 * @returns {boolean} True if the meeting is cancelled
 */
export const isCancelled = (meeting: Meeting): boolean => (
  meeting.cancelled === true || meeting.status === MEETING_STATUS.CANCELED
)

/**
 * Returns true if current time is between meeting.startTime and meeting.endTime
 * Cancelled meetings are never in-progress (checks both cancelled field and status enum)
 * Client-side calculation using browser time
 *
 * @param meeting - Meeting object
 * @param currentTime - Current time (moment object). Defaults to moment() if not provided.
 * @returns {boolean} True if the meeting is currently in progress
 */
export const isInProgress = (meeting: Meeting, currentTime: moment.Moment = moment()): boolean => {
  if (isCancelled(meeting) || !meeting.startTime || !meeting.endTime) {
    return false
  }

  const start = moment(meeting.startTime)
  const end = moment(meeting.endTime)
  if (!start.isValid() || !end.isValid()) {
    return false
  }

  // Compare at minute level for user-friendly behavior
  const currentMinute = currentTime.clone().startOf('minute')
  return currentMinute.isSameOrAfter(start) && currentMinute.isBefore(end)
}

/**
 * Returns true if meeting.endTime <= now
 * Always calculates from endTime for real-time accuracy (ignores stale GraphQL 'past' field)
 * Falls back to GraphQL 'past' field only if endTime is missing or invalid
 *
 * @param meeting - Meeting object
 * @param currentTime - Current time (moment object). Defaults to moment() if not provided.
 * @returns {boolean} True if the meeting has ended
 */
export const isPast = (meeting: Meeting, currentTime: moment.Moment = moment()): boolean => {
  const { endTime, past } = meeting

  const end = endTime ? moment(endTime) : null
  if (!end || !end.isValid()) {
    return typeof past === 'boolean' ? past : false
  }

  return end.isSameOrBefore(currentTime)
}

/**
 * Returns true if meeting starts within the next 15 minutes
 * Cancelled meetings are never starting soon
 *
 * @param meeting - Meeting object
 * @param currentTime - Current time (moment object). Defaults to moment() if not provided.
 * @returns {boolean} True if the meeting starts within the next 15 minutes
 */
export const isStartingSoon = (meeting: Meeting, currentTime: moment.Moment = moment()): boolean => {
  if (isCancelled(meeting) || !meeting.startTime) {
    return false
  }

  const startTime = moment(meeting.startTime)
  if (!startTime.isValid() || !startTime.isAfter(currentTime)) {
    return false
  }

  const secondsUntilStart = startTime.diff(currentTime, 'seconds')
  return secondsUntilStart > 0 && secondsUntilStart <= STARTING_SOON_THRESHOLD_SECONDS
}

/**
 * Determines meeting display status based on current time
 * Priority: CANCELLED → RESCHEDULED → IN_PROGRESS → STARTING_SOON → NO_SHOW (past only) → UPCOMING
 *
 * Note: CANCELLED and RESCHEDULED override all time-based statuses.
 * NO_SHOW only applies to meetings that have ended (past end time).
 * Past meetings with no special status return UPCOMING (empty display text, no badge).
 *
 * @param meeting - Meeting object with status, start_time, end_time, cancelled fields
 * @param currentTime - Current time (moment object). Defaults to moment() if not provided.
 * @returns Meeting status constant from DisplayStatus
 */
export const getMeetingStatus = (meeting: Meeting | null | undefined, currentTime: moment.Moment = moment()): DisplayStatus => {
  if (!meeting) return DISPLAY_STATUS.UPCOMING

  // CANCELLED and RESCHEDULED override all time-based statuses
  if (isCancelled(meeting)) return DISPLAY_STATUS.CANCELLED
  if (meeting.status === MEETING_STATUS.RESCHEDULED) return DISPLAY_STATUS.RESCHEDULED

  // Time-based statuses (only apply if not cancelled/rescheduled)
  if (isInProgress(meeting, currentTime)) return DISPLAY_STATUS.IN_PROGRESS
  if (isStartingSoon(meeting, currentTime)) return DISPLAY_STATUS.STARTING_SOON

  // For past meetings, only NO_SHOW gets a display status; completed past meetings show no badge (UPCOMING)
  if (isPast(meeting, currentTime)) {
    if (meeting.status === MEETING_STATUS.NO_SHOW) return DISPLAY_STATUS.NO_SHOW
    return DISPLAY_STATUS.UPCOMING
  }

  return DISPLAY_STATUS.UPCOMING
}

/**
 * Determines which meeting should be marked as "Up Next"
 * Returns the UUID of the earliest non-canceled, upcoming meeting that is TODAY
 *
 * - Must be today (same day as current date)
 * - Must be in the future (hasn't started yet)
 * - Must not be canceled
 * - Returns the earliest one (by start_time)
 *
 * @param meetings - Array of meetings (from RTK Query cache)
 * @param currentTime - Current time (moment object). Defaults to moment() if not provided.
 * @returns Meeting UUID of the "up next" meeting, or null if none found
 */
export const getUpNextMeetingId = (
  meetings: Meeting[] | null | undefined,
  currentTime: moment.Moment = moment()
): string | null => {
  if (!meetings?.length) return null

  const today = currentTime.format('YYYY-MM-DD')

  const upNextMeeting = meetings
    .filter((meeting) => {
      if (!meeting?.startTime || isCancelled(meeting)) return false

      const startTime = moment(meeting.startTime)
      return startTime.isValid()
        && startTime.format('YYYY-MM-DD') === today
        && currentTime.isBefore(startTime)
    })
    .sort((a, b) => moment(a.startTime!).diff(moment(b.startTime!)))[0]

  return upNextMeeting?.uuid || null
}

/**
 * Finds the index of the most recent in-progress meeting
 * "Most recent" is defined as the meeting with the latest start time that is currently in progress
 * If multiple meetings have the same start time, the one furthest down in the list wins
 *
 * Use case: When multiple overlapping meetings are in progress, determine which one
 * should be highlighted as the primary "current" meeting
 *
 * @param meetings - Array of meetings
 * @param currentTime - Current time (moment object). Defaults to moment() if not provided.
 * @returns {number} Index of the most recent in-progress meeting, or -1 if none found
 */
export const findMostRecentInProgressIndex = (
  meetings: Meeting[],
  currentTime: moment.Moment = moment()
): number => {
  if (!meetings?.length) return -1

  let mostRecentIndex = -1

  // Find the last in-progress meeting by iterating forward and overwriting
  // This naturally handles ties - meetings further down in the list win
  for (let i = 0; i < meetings.length; i += 1) {
    const meeting = meetings[i]
    if (meeting && isInProgress(meeting, currentTime)) {
      mostRecentIndex = i
    }
  }

  return mostRecentIndex
}

/**
 * Returns the i18n key for a display status (en.yml publisher.meetings.header_badge.*).
 * UPCOMING returns '' (no label). Use with I18n.t in the component layer.
 *
 * @returns {string} The i18n translation key for the display status
 */
export const getDisplayLabelKey = (status: DisplayStatus): string => DISPLAY_TEXT_TRANSLATION_KEYS[status] ?? ''

/**
 * Returns locale-aware relative time text for "starting soon" (e.g. "In 10 min").
 * Uses Intl.RelativeTimeFormat only; no I18n. Call from component layer when status is STARTING_SOON.
 *
 * @param startTime - Meeting start time (ISO string)
 * @param currentTime - Current time (moment object)
 * @param locale - Locale for Intl.RelativeTimeFormat (e.g. I18n.locale or user locale)
 * @returns {string} Formatted relative time text (e.g. "In 10 min"), or empty string if invalid
 */
export const getStartingSoonRelativeText = (
  startTime: string,
  currentTime: moment.Moment,
  locale: string = 'en'
): string => {
  const start = moment(startTime)
  if (!start.isValid()) return ''
  const currentMinute = currentTime.clone().startOf('minute')
  const startMinute = start.clone().startOf('minute')
  const minutesUntilStart = startMinute.diff(currentMinute, 'minutes')
  if (minutesUntilStart <= 0) return ''
  const formatter = new Intl.RelativeTimeFormat(intlLocaleFor(locale), { style: 'short' })
  const formatted = formatter.format(minutesUntilStart, 'minute')
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).replace(/\.$/, '')
}
